import { useState, useEffect, useCallback } from 'react'
import { getPaymentStatus } from '../services/paymentService'

/**
 * Hook para fazer polling do status de pagamento
 * Verifica automaticamente a cada X segundos se o pagamento foi confirmado
 *
 * @param {number} paymentId - ID do pagamento para verificar
 * @param {string} token - Token de autenticação
 * @param {number} interval - Intervalo em ms (padrão: 5000 = 5 segundos)
 * @param {boolean} enabled - Se o polling deve estar ativo (padrão: true)
 * @returns {Object} { payment, loading, error, isConfirmed, stopPolling }
 */
export const usePaymentPolling = (paymentId, token, interval = 5000, enabled = true) => {
  const [payment, setPayment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isPolling, setIsPolling] = useState(enabled)

  // Verifica se o pagamento foi confirmado
  const isConfirmed = payment?.status === 'confirmed' || payment?.status === 'received'

  // Função para parar o polling manualmente
  const stopPolling = useCallback(() => {
    setIsPolling(false)
  }, [])

  // Função para verificar o status
  const checkStatus = useCallback(async () => {
    if (!paymentId || !token || !isPolling) return

    try {
      setLoading(true)
      const data = await getPaymentStatus(paymentId, token)
      setPayment(data)
      setError(null)

      // Se foi confirmado, para o polling automaticamente
      if (data.status === 'confirmed' || data.status === 'received') {
        setIsPolling(false)
      }
    } catch (err) {
      setError(err.message)
      console.error('Erro no polling:', err)
    } finally {
      setLoading(false)
    }
  }, [paymentId, token, isPolling])

  // Efeito para fazer polling
  useEffect(() => {
    if (!isPolling) return

    // Executa imediatamente
    checkStatus()

    // Configura intervalo
    const intervalId = setInterval(checkStatus, interval)

    // Cleanup
    return () => {
      clearInterval(intervalId)
    }
  }, [checkStatus, interval, isPolling])

  return {
    payment,
    loading,
    error,
    isConfirmed,
    stopPolling,
    isPolling
  }
}

export default usePaymentPolling
